import cx from "classnames";
import { Tooltip } from "components/Tooltip/Tooltip";
import isString from "lodash/isString";
import { toggleDrawer } from "reducers/drawer";
import type { RootState } from "services/store";
import { useDispatch, useSelector } from "utils/redux";

import type { InfoPillData } from "./InfoPillTypes";

type RushType = "24-hour" | "3-day" | "rush" | null;

type HeaderInfoPillProps = {
  data: Record<string, InfoPillData>;
  className?: string;
  rushType?: RushType;
};

const MAX_STRING_LENGTH = 40;

function truncateValue(value: string) {
  if (value.length > MAX_STRING_LENGTH) {
    return `${value.substring(0, MAX_STRING_LENGTH)}...`;
  }
  return value;
}

const HeaderInfoPill = ({
  data,
  className,
  rushType = null,
}: HeaderInfoPillProps) => {
  const dispatch = useDispatch();
  const drawerState = useSelector((state: RootState) => state.drawer);

  const handleAction = (action: InfoPillData["action"]) => {
    if (!action) return;

    switch (action.type) {
      case "drawer":
        dispatch(toggleDrawer(action.drawerType));
        break;
      case "custom":
        action.handler();
        break;
    }
  };

  const getDrawerActiveState = (drawerType: string): boolean => {
    switch (drawerType) {
      case "clientInfo":
        return drawerState.showClientInfoDrawer;
      case "documents":
        return drawerState.showDocumentsDrawer;
      case "tasks":
        return drawerState.showTasksDrawer;
      case "email":
        return drawerState.showEmailDrawer;
      // Add more drawer types as needed
      default:
        return false;
    }
  };

  return (
    <div
      className={cx("infopill", className, {
        "infopill--rush": rushType !== null,
      })}
      data-rush-type={rushType || undefined}
    >
      {Object.entries(data).map(([label, context], index) => {
        const hasAction = Boolean(context.action);
        const isDrawerAction = context.action?.type === "drawer";
        const isDrawerActive =
          isDrawerAction &&
          context.action?.type === "drawer" &&
          getDrawerActiveState(context.action.drawerType);

        const handleClick = () => handleAction(context.action);

        return (
          <div
            className={cx("infopill__item", {
              "pill-clickable": hasAction,
              "pill-active": isDrawerActive,
            })}
            key={index}
            onClick={hasAction ? handleClick : undefined}
          >
            <div className="infopill__item__label">{label}:</div>
            {context.disableTooltip ? (
              <div className={cx("infopill__item__value", context.classNames)}>
                {isString(context.value)
                  ? truncateValue(context.value?.toString() || "Not Available")
                  : context.value}
              </div>
            ) : (
              <Tooltip
                content={
                  context.tooltip ||
                  (isString(context.value)
                    ? context.value?.toString() || "Not Available"
                    : "Not Available")
                }
                place="right"
                html={context.tooltipHtml || false}
              >
                <div
                  className={cx("infopill__item__value", context.classNames)}
                >
                  {isString(context.value)
                    ? truncateValue(
                        context.value?.toString() || "Not Available",
                      )
                    : context.value}
                </div>
              </Tooltip>
            )}
            {hasAction && !isDrawerAction && (
              <a className={"material-icons"} onClick={handleClick}>
                &nbsp;link
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default HeaderInfoPill;
